import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Award, Star, Trophy } from "lucide-react";
import api from "@/lib/api";
import TierBadge from "@/components/locanest/TierBadge";

export default function VendorsPage() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/vendors/top").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Recognised on LocaNest</div>
      <div className="mt-2 flex items-center gap-3">
        <Trophy size={30} className="text-[#C85A32]" />
        <h1 className="font-display text-4xl md:text-5xl">Top Vendors</h1>
      </div>
      <p className="text-[#5C6B7A] mt-2 max-w-2xl">
        Ratings only come from deals completed through LocaNest. The higher an owner ranks here, the more inquiries they earn — no ads, no shortcuts.
      </p>

      {items === null ? (
        <div className="mt-10 text-[#5C6B7A]">Loading leaderboard…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 bg-[#F0EBE1] rounded-lg p-12 text-center text-[#5C6B7A]" data-testid="vendors-empty">
          No verified vendors yet. Owners appear here after they clear verification and (for Trusted / Top) complete platform deals.
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="vendors-grid">
          {items.map((v, i) => (
            <div key={v.id} className="bg-[#F0EBE1] rounded-lg overflow-hidden border border-[#1A1F2C]/10 flex flex-col" data-testid={`vendor-card-${v.id}`}>
              {v.sample_property?.photo && (
                <div className="aspect-[16/9] bg-[#1A1F2C]/5">
                  <img src={v.sample_property.photo} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B7A]">#{i + 1}</div>
                    <div className="font-display text-xl truncate">{v.name}</div>
                    {v.business_name && <div className="text-xs text-[#5C6B7A]">{v.business_name}</div>}
                  </div>
                  <TierBadge tier={v.vendor_tier} />
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-[#FDFBF7] rounded p-2">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C6B7A]">Rating</div>
                    <div className="inline-flex items-center gap-1 font-display text-lg">
                      <Star size={14} fill="#C85A32" color="#C85A32" /> {v.rating_owner_count > 0 ? v.rating_owner_avg.toFixed(1) : "—"}
                      <span className="text-[11px] text-[#5C6B7A] font-normal ml-1">({v.rating_owner_count})</span>
                    </div>
                  </div>
                  <div className="bg-[#FDFBF7] rounded p-2">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C6B7A]">Deals done</div>
                    <div className="font-display text-lg">{v.completed_deals_count}</div>
                  </div>
                </div>

                {v.sample_property && (
                  <Link
                    to={`/property/${v.sample_property.id}`}
                    className="mt-4 text-xs text-[#C85A32] hover:underline"
                    data-testid={`vendor-listing-${v.id}`}
                  >
                    View a listing → {v.sample_property.title}
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-14 bg-[#D4DEC6]/25 border border-[#2B3A67]/20 rounded-lg p-6 flex items-start gap-3">
        <Crown className="text-[#C85A32] shrink-0 mt-1" />
        <div>
          <div className="font-display text-lg">How the tiers work</div>
          <ul className="mt-2 space-y-1 text-sm text-[#1A1F2C]/85 list-disc pl-5">
            <li><strong>Top Vendor</strong> — 20+ completed platform deals, average rating ≥ 4.5★.</li>
            <li><strong>Trusted</strong> — 5+ completed platform deals, average rating ≥ 4.0★.</li>
            <li><strong>Verified</strong> — Aadhaar &amp; PAN approved (plus business proof for commercial listings).</li>
            <li><strong>New</strong> — Fresh owners get a fair-visibility slot every fifth position in search.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
